import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Breadcrumb, Button, Form, Input, Modal, Pagination, Table, Upload, message, Popconfirm, Progress } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { UploadOutlined } from '@ant-design/icons';
import { api, uploadDirectToBlob } from '../../utils/api';

const { TextArea } = Input;

interface VersionItem {
  id: number;
  version: string;
  changes: string;
  releaseDate: string; // ISO string from backend
}

const formatDate = (iso: string) => {
  const d = new Date(iso);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}/${m}/${day}`;
};

const pageSize = 10;

const AdminAppVersionsPage: React.FC = () => {
  const { id } = useParams();

  const [data, setData] = useState<VersionItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [appName, setAppName] = useState<string>('');
  const [uploadProgress, setUploadProgress] = useState(0);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api.get(`/api/admin/apps/${id}/versions`, { params: { page, pageSize } });
        if (!alive) return;
        setData(res.data.items);
        setTotal(res.data.total);
      } catch (e) {
        setData([]);
        setTotal(0);
      }
    })().catch(() => {
      setData([]);
      setTotal(0);
    });
    return () => { alive = false; };
  }, [id, page]);

  // Fetch app name to build folder prefix like admin-apps/<slug>/installer/<version>
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api.get(`/api/admin/apps/${id}`);
        if (!alive) return;
        setAppName(res.data?.name || '');
      } catch {
        setAppName('');
      }
    })();
    return () => { alive = false; };
  }, [id]);

  const items = data;

  const columns: ColumnsType<VersionItem> = [
    { title: 'バージョン', dataIndex: 'version', key: 'version' },
    { title: '日付', dataIndex: 'releaseDate', key: 'releaseDate', render: (v: string) => formatDate(v) },
    { title: '変更内容', dataIndex: 'changes', key: 'changes' },
    {
      title: 'アクション',
      key: 'actions',
      width: 140,
      render: (_, record) => (
        <div className="flex gap-2">
          <Popconfirm
            title="このバージョンを削除しますか？"
            description={`v${record.version} を削除すると復元できません。よろしいですか？`}
            okText="削除"
            okButtonProps={{ danger: true }}
            cancelText="キャンセル"
            onConfirm={async () => {
              try {
                await api.delete(`/api/admin/apps/${id}/versions/${record.id}`);
                message.success('バージョンを削除しました。');
                const res = await api.get(`/api/admin/apps/${id}/versions`, { params: { page, pageSize } });
                setData(res.data.items);
                setTotal(res.data.total);
              } catch {
                message.error('削除に失敗しました。');
              }
            }}
          >
            <Button danger>削除</Button>
          </Popconfirm>
        </div>
      )
    }
  ];

  // Modal state
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm();
  const [installFile, setInstallFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);

  const isValid = () => {
    const v = form.getFieldsValue();
    return !!(v.version && v.changes && installFile);
  };

  const onSave = async () => {
    try {
      const values = await form.validateFields();
      if (!installFile) return;
      setSaving(true);
      setUploadProgress(0);
      // Build per-app slugged prefix and include version subfolder
      const slug = String(appName || '')
        .toLowerCase()
        .trim()
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-]/g, '');
      const basePrefix = slug ? `admin-apps/${slug}` : 'admin-apps/unknown-app';
      // Upload installer directly to Blob Storage under installer/<version>
      const direct = await uploadDirectToBlob(installFile, `${basePrefix}/installer/${values.version}`, {
        onProgress: (ev) => {
          const pct = Math.round((ev.loadedBytes / (installFile.size || 1)) * 100);
          setUploadProgress(pct);
        },
        blockSizeMB: 8,
        concurrency: 8,
      });
      const installUrl = direct.url;
      await api.post(`/api/admin/apps/${id}/versions`, {
        version: values.version,
        changes: values.changes,
        installerUrl: installUrl,
      });
      message.success('バージョンを更新しました。');
      setOpen(false);
      form.resetFields();
      setInstallFile(null);
      setUploadProgress(0);
      // refresh list
      const res = await api.get(`/api/admin/apps/${id}/versions`, { params: { page, pageSize } });
      setData(res.data.items);
      setTotal(res.data.total);
    } catch (e) {
      // validation errors are handled by antd; other errors show message
      if ((e as any).errorFields == null) {
        message.error('保存に失敗しました。');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex w-full flex-col gap-4">
      <h2 className="text-xl font-semibold text-left">バージョン一覧</h2>
      <Breadcrumb
        items={[
          { title: <Link to="/admin">アプリ一覧</Link> },
          { title: 'バージョン一覧' },
        ]}
      />

      <div className="overflow-hidden rounded-lg border border-gray-200">
        <Table
          rowKey="id"
          dataSource={items}
          columns={columns}
          pagination={false}
        />
      </div>

      <div className="flex items-center justify-end">
        <div className="flex items-center gap-2">
          <div className="text-sm text-gray-600">全 {total}件</div>
          <Pagination
            current={page}
            pageSize={pageSize}
            total={total}
            onChange={(p) => setPage(p)}
            showSizeChanger={false}
          />
        </div>
      </div>

      <div className="flex justify-start">
        <Button type="primary" onClick={() => setOpen(true)}>バージョン更新</Button>
      </div>

      <Modal
        title="バージョンの更新"
        open={open}
        onCancel={() => setOpen(false)}
        footer={null}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" requiredMark={false}>
          <Form.Item
            label={<span>バージョン <span className="text-red-500">※</span></span>}
            name="version"
            rules={[
              { required: true, message: 'バージョンを入力してください' },
              { pattern: /^\d+\.\d+\.\d+$/, message: 'バージョンは数字の形式 x.y.z で入力してください（例: 1.2.3）' },
            ]}
          >
            <Input placeholder="例) 1.2.3" />
          </Form.Item>
          <Form.Item
            label={<span>変更内容 <span className="text-red-500">※</span></span>}
            name="changes"
            rules={[{ required: true, message: '' }]}
          >
            <TextArea rows={5} placeholder="変更点を入力" />
          </Form.Item>
          <Form.Item label={<span>インストールファイルのアップロード <span className="text-red-500">※</span></span>}>
            <Upload
              beforeUpload={(file: File) => { setInstallFile(file); return false; }}
              maxCount={1}
              showUploadList={installFile ? { showPreviewIcon: false } : false}
              accept=".exe,.msi,.zip,.rar,.7z,.dmg,.pkg"
            >
              <Button icon={<UploadOutlined />} className="w-[378px] h-[46px] rounded-[6px] border px-[120px]">アップロード</Button>
            </Upload>
            {installFile && <div className="mt-2 text-sm text-gray-600">{installFile.name}</div>}
            {uploadProgress > 0 && uploadProgress < 100 && (
              <div className="mt-2">
                <Progress percent={uploadProgress} size="small" className="w-full max-w-[378px]" />
              </div>
            )}
          </Form.Item>

          <div className="mt-4 flex justify-end gap-3">
            <Button onClick={() => setOpen(false)} disabled={saving}>キャンセル</Button>
            <Button type="primary" onClick={onSave} loading={saving} disabled={!isValid()}>
              {saving && uploadProgress > 0 && uploadProgress < 100 ? `アップロード中… ${uploadProgress}%` : '保存'}
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default AdminAppVersionsPage;
